/**
 * Règles d'âge PURES — source unique SaaS ↔ widget.
 *
 * Les tranches (« bandes ») viennent du référentiel de chaque transporteur,
 * exposées par le backend dans `journey.age[]` (aplaties depuis
 * `Provider::getAges()`). Depuis l'assainissement de la table `age`, elles sont
 * **contiguës et exclusives** par provider : le front les lit telles quelles,
 * sans décalage ni rattrapage.
 *
 * Règle métier centrale : **l'âge d'un passager s'évalue à la date de départ du
 * voyage**. Sur un aller-retour, il doit rester dans sa bande à l'aller ET au
 * retour — un anniversaire entre les deux legs le ferait basculer de type, ce
 * que le transporteur refuserait sur l'un des deux.
 *
 * Aucune dépendance Vue / store / I/O.
 */

import type { Journey, Passenger } from '../types'

/** Tranche d'âge d'un type de passager, bornes incluses. */
export interface AgeBand {
  min: number
  max: number
}

/** Les trois types de passagers du modèle FerryGate. */
export type PaxKind = 'Adult' | 'Child' | 'Baby'

/** Bandes résolues pour les trois types (`null` = le provider n'en déclare pas). */
export type AgeBands = Record<PaxKind, AgeBand | null>

/** Motif de rejet d'une date de naissance. L'UI traduit le code. */
export type BirthDateErrorCode = 'REQUIRED' | 'INVALID' | 'FUTURE' | 'OUT_OF_BAND'

export type BirthDateResult =
  | { ok: true }
  | { ok: false; code: BirthDateErrorCode; band: AgeBand | null }

// ════════════════════════════════════════════════════════════════════════
//  Dates — arithmétique en composantes Y/M/D, jamais en timestamp local.
//
//  Travailler en `Date` locale exposerait à des décalages de fuseau (un
//  `new Date('2026-08-12')` est minuit UTC, qui peut être le 11 août en local).
//  Toute l'arithmétique passe donc par `Date.UTC`.
// ════════════════════════════════════════════════════════════════════════

interface Ymd {
  y: number
  m: number
  d: number
}

/**
 * Parse tolérant : `YYYY-MM-DD`, un datetime ISO (`…THH:mm:ss`, la partie heure
 * est ignorée) ou `DD/MM/YYYY`. Les deux stacks divergent — le SaaS saisit la
 * date de naissance en texte `DD/MM/YYYY`, le widget en `<input type="date">`
 * donc `YYYY-MM-DD` — et les `depart_at` backend sont des datetimes ISO.
 * Retourne `null` sur toute valeur non exploitable ou calendairement invalide
 * (31/02, mois 13…).
 */
export function parseDateLoose(value: string | null | undefined): Ymd | null {
  if (typeof value !== 'string') return null
  const raw = value.trim()
  if (raw === '') return null

  let y: number, m: number, d: number

  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw)
  if (iso) {
    y = Number(iso[1]); m = Number(iso[2]); d = Number(iso[3])
  } else {
    const fr = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(raw)
    if (!fr) return null
    d = Number(fr[1]); m = Number(fr[2]); y = Number(fr[3])
  }

  // Rejette les dates calendairement fausses (JS déborderait silencieusement :
  // le 31/02 deviendrait le 03/03).
  const probe = new Date(Date.UTC(y, m - 1, d))
  if (
    probe.getUTCFullYear() !== y
    || probe.getUTCMonth() !== m - 1
    || probe.getUTCDate() !== d
  ) return null

  return { y, m, d }
}

/** `Ymd` → `YYYY-MM-DD`, format des attributs `min`/`max` d'un input date. */
export function formatYmd(v: Ymd): string {
  return `${String(v.y).padStart(4, '0')}-${String(v.m).padStart(2, '0')}-${String(v.d).padStart(2, '0')}`
}

function toUtc(v: Ymd): number {
  return Date.UTC(v.y, v.m - 1, v.d)
}

function fromUtc(ms: number): Ymd {
  const d = new Date(ms)
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate() }
}

function addDays(v: Ymd, days: number): Ymd {
  return fromUtc(toUtc(v) + days * 86400000)
}

/**
 * Recule de `years` années à date constante. Le 29 février d'une année non
 * bissextile déborde au 1er mars — comportement JS standard, assumé : l'écart
 * d'un jour est sans effet sur une borne de tranche d'âge.
 */
function minusYears(v: Ymd, years: number): Ymd {
  return fromUtc(Date.UTC(v.y - years, v.m - 1, v.d))
}

function compare(a: Ymd, b: Ymd): number {
  return toUtc(a) - toUtc(b)
}

// ════════════════════════════════════════════════════════════════════════
//  Âge
// ════════════════════════════════════════════════════════════════════════

/**
 * Âge révolu à une date de référence, ou `null` si l'une des deux dates est
 * inexploitable. Un âge négatif (naissance postérieure à la référence) est
 * retourné tel quel — c'est {@link validateBirthDate} qui le qualifie.
 */
export function ageAt(
  birthDate: string | null | undefined,
  referenceDate: string | null | undefined,
): number | null {
  const birth = parseDateLoose(birthDate)
  const ref = parseDateLoose(referenceDate)
  if (!birth || !ref) return null
  let age = ref.y - birth.y
  if (ref.m < birth.m || (ref.m === birth.m && ref.d < birth.d)) age--
  return age
}

// ════════════════════════════════════════════════════════════════════════
//  Bandes
// ════════════════════════════════════════════════════════════════════════

/**
 * Bande d'un type de passager, consolidée puis intersectée.
 *
 *   1. Pour chaque journey, ses entrées `age[]` du type demandé sont
 *      consolidées en une bande `[min(min_i), max(max_i)]`. Cela absorbe les
 *      providers qui déclarent des sous-tranches (Jeunesse / Adulte / Senior
 *      côté adulte, Bébé / Nourrisson côté bébé) : le sélecteur n'expose qu'un
 *      compteur par type, la sous-tranche est l'affaire du backend.
 *   2. Les bandes des journeys retenus sont intersectées :
 *      `min = max(min_j)`, `max = min(max_j)`.
 *
 * Les journeys qui ne déclarent aucune entrée du type sont **ignorés** — sans
 * quoi un seul provider sans tarif bébé supprimerait le type pour toute la
 * route.
 *
 * `opts.company` restreint aux journeys d'une compagnie donnée. À utiliser dès
 * que la compagnie est connue (tunnel de réservation : le sailing est choisi),
 * pour valider contre la bande réellement applicable plutôt que contre une
 * intersection inutilement restrictive.
 *
 * Retourne `null` si aucune bande n'existe ou si l'intersection est vide.
 */
export function resolveAgeBand(
  journeys: readonly Journey[] | null | undefined,
  type: PaxKind,
  opts?: { company?: string | null },
): AgeBand | null {
  const wanted = normalizeCompany(opts?.company)
  let min = Number.NEGATIVE_INFINITY
  let max = Number.POSITIVE_INFINITY
  let found = false

  for (const journey of journeys ?? []) {
    if (wanted && normalizeCompany(journeyCompany(journey)) !== wanted) continue

    let lo = Number.POSITIVE_INFINITY
    let hi = Number.NEGATIVE_INFINITY
    for (const entry of journey?.age ?? []) {
      if (entry?._type !== type) continue
      const entryMin = Number(entry.min)
      const entryMax = Number(entry.max)
      if (!Number.isFinite(entryMin) || !Number.isFinite(entryMax)) continue
      if (entryMin < lo) lo = entryMin
      if (entryMax > hi) hi = entryMax
    }
    if (lo > hi) continue

    found = true
    if (lo > min) min = lo
    if (hi < max) max = hi
  }

  if (!found || max < min) return null
  return { min, max }
}

/** Les trois bandes d'un coup — ce que consomment les composants. */
export function resolveAgeBands(
  journeys: readonly Journey[] | null | undefined,
  opts?: { company?: string | null },
): AgeBands {
  return {
    Adult: resolveAgeBand(journeys, 'Adult', opts),
    Child: resolveAgeBand(journeys, 'Child', opts),
    Baby: resolveAgeBand(journeys, 'Baby', opts),
  }
}

/**
 * Une tranche TARIFAIRE proposée à la sélection : une ligne du sélecteur de
 * passagers.
 *
 * Le découpage appartient au transporteur — Armas facture séparément Jeunesse
 * (12-25) et Senior (60-120), avec des tarifs réduits. Le backend
 * (`App\Service\Journey\PaxTypeResolver`) normalise son référentiel dans
 * `journey.pax_types` ; le front l'affiche tel quel et renvoie le `param` de la
 * tranche choisie, que le provider revalide avant de l'émettre.
 */
export interface PaxBand extends AgeBand {
  type: PaxKind
  /** Code tranche du transporteur (Armas : `J`, `A`, `M`, `N`, `B`). */
  param: string
  /** Libellé brut du référentiel — présentable seulement en dernier recours. */
  name: string
  /** `standard` | `junior` | `senior` — clé de traduction du libellé. */
  tier: string
  /** Âge donné à un passager créé dans cette tranche. */
  defaultAge: number
}

/**
 * Comment nommer une tranche dans le sélecteur — chaque front traduit ensuite
 * `tier` / `type` avec ses propres clés i18n.
 *
 *   - `tier` (`junior` / `senior`) : rang normalisé par le backend ;
 *   - `type` : la tranche ordinaire de son type (« Adulte », « Bébés »), quand
 *     elle est la seule de son rang — inutile de la surcharger ;
 *   - `name` : libellé brut du référentiel, seul moyen de départager deux
 *     tranches ordinaires d'un même type (Meridionale : « Bébé » 0-1 et
 *     « Nourrisson » 2-3).
 */
export type PaxBandLabel =
  | { tier: 'junior' | 'senior' }
  | { type: PaxKind }
  | { name: string }

export function paxBandLabel(band: PaxBand, all: readonly PaxBand[]): PaxBandLabel {
  if (band.tier === 'junior' || band.tier === 'senior') return { tier: band.tier }

  const peers = all.filter((b) => b.type === band.type && b.tier === band.tier)
  if (peers.length > 1 && band.name !== '') return { name: band.name }

  return { type: band.type }
}

/** Repli générique quand aucune donnée d'âge n'est exploitable. */
const FALLBACK: Record<PaxKind, { param: string; age: number }> = {
  Adult: { param: 'A', age: 30 },
  Child: { param: 'C', age: 10 },
  Baby: { param: 'B', age: 1 },
}

const KINDS: readonly PaxKind[] = ['Adult', 'Child', 'Baby']

/**
 * Tranches à proposer, dans l'ordre d'affichage — **du plus âgé au plus jeune**
 * (Senior → Adulte → Jeunesse → Enfant → Bébé). Cet ordre est posé par le
 * backend (`PaxTypeResolver`) et conservé tel quel : les deux fronts rendent la
 * liste sans la retrier.
 *
 * On lit le **premier journey** qui déclare une grille (filtré par compagnie si
 * `opts.company` est fourni) plutôt que d'intersecter : deux transporteurs ont
 * des grilles tarifaires différentes, et fusionner « Jeunesse 12-25 » d'Armas
 * avec « Adulte 14-120 » de Baleària ne donnerait aucune tranche réelle. Si la
 * compagnie retenue au final n'a pas la tranche choisie, son `param` sort de son
 * référentiel et le provider retombe sur le code du type — sans erreur de prix.
 */
export function resolvePaxBands(
  journeys: readonly Journey[] | null | undefined,
  opts?: { company?: string | null },
): PaxBand[] {
  const wanted = normalizeCompany(opts?.company)

  for (const journey of journeys ?? []) {
    if (wanted && normalizeCompany(journeyCompany(journey)) !== wanted) continue

    const bands = (journey?.pax_types ?? [])
      .filter((t) => KINDS.includes(t?.type as PaxKind))
      .map((t) => ({
        type: t.type as PaxKind,
        param: String(t.param ?? ''),
        name: String(t.name ?? ''),
        tier: String(t.tier ?? 'standard'),
        min: Number(t.min),
        max: Number(t.max),
        defaultAge: Number(t.default_age),
      }))
      .filter((b) => Number.isFinite(b.min) && Number.isFinite(b.max) && b.max >= b.min)

    if (bands.length > 0) return bands
  }

  // Repli — backend pas encore déployé. Une tranche générique par type, depuis
  // les bandes consolidées : on n'invente PAS de découpage tarifaire, c'est au
  // transporteur de le déclarer.
  return KINDS.flatMap((type) => {
    const band = resolveAgeBand(journeys, type, opts)
    if (!band) return []
    return [{
      type,
      param: FALLBACK[type].param,
      name: '',
      tier: 'standard',
      min: band.min,
      max: band.max,
      defaultAge: Math.min(Math.max(FALLBACK[type].age, band.min), band.max),
    }]
  })
}

function journeyCompany(journey: Journey | null | undefined): string {
  const c = journey?.company
  if (typeof c === 'string') return c
  return String(c?.name ?? c?.id ?? '')
}

function normalizeCompany(value: string | null | undefined): string {
  return String(value ?? '').trim().toLowerCase()
}

// ════════════════════════════════════════════════════════════════════════
//  Bornes de saisie & validation
// ════════════════════════════════════════════════════════════════════════

/**
 * Fenêtre de naissance admissible pour une bande, aux dates de départ données.
 *
 * Pour une date de référence `D` et une bande `[min, max]` :
 *   - né au plus tard le `D − min ans`      (sinon l'âge serait < min)
 *   - né au plus tôt  le `D − (max+1) ans + 1 jour` (sinon l'âge serait > max)
 *
 * Avec plusieurs départs (aller + retour), la fenêtre est l'**intersection** :
 * le passager doit être dans sa bande à chaque leg. La borne haute vient donc
 * du départ le plus tôt, la borne basse du départ le plus tard.
 *
 * Retourne `null` si la bande est absente, si aucune date de référence n'est
 * exploitable, ou si l'intersection est vide (bande impossible à satisfaire sur
 * l'ensemble des legs — un passager pile sur une frontière d'âge).
 *
 * Les bornes sont au format `YYYY-MM-DD`, directement utilisables en
 * `min`/`max` d'un `<input type="date">`.
 */
export function birthDateBounds(
  band: AgeBand | null | undefined,
  referenceDates: readonly (string | null | undefined)[],
): { min: string; max: string } | null {
  if (!band) return null

  let earliest: Ymd | null = null
  let latest: Ymd | null = null

  for (const raw of referenceDates ?? []) {
    const ref = parseDateLoose(raw)
    if (!ref) continue
    const lo = addDays(minusYears(ref, band.max + 1), 1)
    const hi = minusYears(ref, band.min)
    if (!earliest || compare(lo, earliest) > 0) earliest = lo
    if (!latest || compare(hi, latest) < 0) latest = hi
  }

  if (!earliest || !latest || compare(earliest, latest) > 0) return null
  return { min: formatYmd(earliest), max: formatYmd(latest) }
}

/**
 * Confronte une date de naissance au type déclaré, à chaque date de départ.
 *
 * Une bande absente (`null`) ne bloque **pas** : plusieurs providers n'ont
 * aucune tranche configurée, et refuser leurs dossiers coûterait des ventes
 * pour une donnée manquante côté référentiel. Idem si aucune date de départ
 * n'est exploitable — on ne bloque jamais sur un contexte incomplet.
 */
export function validateBirthDate(input: {
  birthDate: string | null | undefined
  band: AgeBand | null | undefined
  referenceDates: readonly (string | null | undefined)[]
}): BirthDateResult {
  const band = input.band ?? null

  const birth = parseDateLoose(input.birthDate)
  if (!input.birthDate || String(input.birthDate).trim() === '') {
    return { ok: false, code: 'REQUIRED', band }
  }
  if (!birth) return { ok: false, code: 'INVALID', band }

  const refs = (input.referenceDates ?? [])
    .map((r) => parseDateLoose(r))
    .filter((r): r is Ymd => r !== null)

  if (refs.length === 0) return { ok: true }

  for (const ref of refs) {
    if (compare(birth, ref) > 0) return { ok: false, code: 'FUTURE', band }
  }

  if (!band) return { ok: true }

  for (const ref of refs) {
    const age = ageAt(formatYmd(birth), formatYmd(ref))
    if (age === null) continue
    if (age < band.min || age > band.max) {
      return { ok: false, code: 'OUT_OF_BAND', band }
    }
  }

  return { ok: true }
}

/**
 * Raccourci pour un passager du store : résout sa bande puis valide sa date de
 * naissance. Le `type` inconnu retombe sur `Adult` (défaut du modèle backend).
 */
export function validatePassengerBirthDate(
  passenger: Pick<Passenger, 'type' | 'birth_date'>,
  bands: AgeBands,
  referenceDates: readonly (string | null | undefined)[],
): BirthDateResult {
  const kind = (['Adult', 'Child', 'Baby'] as const).includes(passenger?.type as PaxKind)
    ? (passenger.type as PaxKind)
    : 'Adult'
  return validateBirthDate({
    birthDate: passenger?.birth_date,
    band: bands[kind],
    referenceDates,
  })
}
